











































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { InputFile } from "@scrile/api-provider/dist/api/FileProvider";
import { UserVerification } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { getFileMimeType } from "@scrile/tools/dist/lib/FileHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles from "@/hooks/useFiles";
import useUserVerification from "@/hooks/useUserVerification";
import providers from "@/lib/api";
import FileUploader, { calculateProgress, showDefaultUploadError } from "@/lib/FileUploader";
import config from "@/config";
import { FormValues } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseCheckbox from "@/components/BaseCheckbox";
import BaseForm from "@/components/BaseForm";
import PayeeInfoInputFile from "./PayeeInfoInputFile.vue";
import PersonalIdExampleConfirm from "./PersonalIdExampleConfirm.vue";
import { personalIdFields } from "../config";

export default defineComponent({
  name: "PersonalIdForm",
  components: {
    BaseButton,
    BaseCheckbox,
    PayeeInfoInputFile,
    BaseForm,
    PersonalIdExampleConfirm,
  },
  setup(_, { emit }) {
    const { authUserStrict } = useAuthUser();
    const { search } = useUserVerification();
    const { deleteFile } = useFiles();
    const userVerificationStatus = ref<UserVerification | null>(null);
    const isSensitiveAgreement = ref(false);
    const isAcknowledgeAgreement = ref(false);
    const showExampleModal = ref(false);

    const progress = ref<Record<string, { progress: number }>>({
      id_photo: {
        progress: 100,
      },
      photo_with_id: {
        progress: 100,
      },
    });
    const fields = computed(() => personalIdFields());

    const values = computed<FormValues>(() => {
      return {
        ...(config.featureNSFW ? { sensitiveContent: isSensitiveAgreement.value } : {}),
        ...(config.featureNSFW ? { acknowledgeAgreement: isAcknowledgeAgreement.value } : {}),
        id_photo: {
          data: userVerificationStatus.value?.idPhoto || null,
        },
        photo_with_id: {
          data: userVerificationStatus.value?.photoWithId || null,
        },
      };
    });

    const getUserVerification = async () => {
      let data = await search({ userIds: [authUserStrict.value.id] });
      userVerificationStatus.value = data.result.find(i => i.userId === authUserStrict.value.id) || null;
    };

    const addFileToQueue = async (type: string, file: File): Promise<any> => {
      return new Promise<any>(async (resolve, reject) => {
        const response = await providers.FileProvider.getUploadUrl({
          fileIds: [{ subjectId: authUserStrict.value.id, type }],
        });

        FileUploader.addToQueue([
          {
            file: file,
            fileType: getFileMimeType(file),
            id: `${response[0].fileId.subjectId}_${response[0].fileUpload.uuid}`,
            urlPart: response[0].fileUpload.urlPart,
            onSuccess: async () => {
              progress.value[type].progress = 100;
              resolve(response[0]);
            },
            onUploadProgress: p => (progress.value[type].progress = calculateProgress(p)),
            onError: e => {
              showDefaultUploadError();
              reject(e);
            },
          },
        ]);
      });
    };
    const checkValid = () => {
      const container = fields.value.find(el => el.name === "photo_container");

      if (config.featureNSFW && (!isSensitiveAgreement.value || !isAcknowledgeAgreement.value)) {
        return emit("setValid", false);
      }
      if (!container) return emit("setValid", true);

      if (
        container.value.find((i: { name: string }) => i.name === "id_photo")?.meta?.required &&
        !userVerificationStatus.value?.idPhoto
      ) {
        return emit("setValid", false);
      }
      if (
        container.value.find((i: { name: string }) => i.name === "photo_with_id")?.meta?.required &&
        !userVerificationStatus.value?.photoWithId
      ) {
        return emit("setValid", false);
      }
      return emit("setValid", true);
    };

    const upload = async (file: FileList | null, type: string) => {
      if (file) {
        progress.value[type].progress = 0;
        const uploadData = await addFileToQueue(type, file[0]);
        const data: InputFile = {
          fileId: uploadData.fileId,
          uuid: uploadData.fileUpload.uuid,
        };
        await providers.FileProvider.update({ data });
      } else {
        await deleteFile({ fileId: { subjectId: authUserStrict.value.id, type: type } });
      }
      await getUserVerification();
      checkValid();
    };
    const inputSensitive = (v: boolean) => {
      isSensitiveAgreement.value = v;
      checkValid();
    };
    const inputAcknowledge = (v: boolean) => {
      isAcknowledgeAgreement.value = v;
      checkValid();
    };

    const getData = async () => {
      await getUserVerification();
      checkValid();
    };

    const created = async () => {
      emit("setValid", true);
      try {
        emit("setLoading", true);
        await getData();
      } finally {
        emit("setLoading", false);
      }
    };

    created();

    return {
      showExampleModal,
      values,
      fields,
      progress,
      inputSensitive,
      inputAcknowledge,
      upload,
    };
  },
});
